// ============================================================================
// Componente: Contabilidade.js (MÓDULO CONTABILIDADE)
// Objetivo: Casco do módulo Contábil — mesmo padrão visual/estrutural do módulo
//           Financeiro (uma Sidebar + cabeçalho + barra de abas), na paleta VERDE.
//           As ferramentas contábeis (RTB12, P&L, Conferência) vivem aqui como
//           conteúdo EMBUTIDO (prop `embedded`), igual o Petty Cash vive dentro do
//           Financeiro — em vez de páginas soltas no hub de Automações.
// ============================================================================

import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import Sidebar from '../Sidebar';
import {
    ArrowRight, Calculator, LayoutDashboard,
    DollarSign, Zap
} from 'lucide-react';
import HelpCenter from '../HelpCenter';
import ModuloHeader from '../ModuloHeader';

// RTB12 é tile próprio (estilo Petty Cash). P&L e Conferência ficam no submódulo
// de Automações.
//
// ⚠️ A aba RELATÓRIOS foi ARQUIVADA em 2026-09-01 e sumiu inteira: ela só tinha o
//    Fechamento Mensal (visão Contábil), que está em
//    backup/contabilidade/relatorio-fechamento/ — front, rota e schema, com a
//    receita de como religar. ⚠️ NÃO confundir com o Fechamento do FINANCEIRO,
//    que segue no ar: é outra procedure (sp_gerar_relatorios_fechamento) e outro
//    módulo. Ver docs/PLANO.md › Trilho A › "Arquivar funcionalidade em desuso".
import AutomacaoRTB12 from './automacoes/ContabilidadeAutomacaoRTB12';
import ContabilidadeAutomacoes from './automacoes/ContabilidadeAutomacoes';

// 1. PERMISSÕES POR CARD — mesmos perfis que acessavam as telas no hub antigo.
//    (RTB12 inclui Fiscal.)
//
// ⚠️ `Financeiro` SAIU desta lista junto com a aba Relatórios (2026-09-01): era a
//    única aba que o citava, então sem ela o perfil entraria num módulo sem
//    nenhuma aba visível. Ele também saiu do `allowedRoles` da rota no App.js.
const PERMISSIONS = {
    'rtb12':      ['Contabil', 'Gerente Adm', 'Admin', 'Diretoria', 'Fiscal'],
    // Automações agrupa P&L e Conferência — união dos perfis das duas telas.
    'automacoes': ['Contabil', 'Gerente Adm', 'Admin', 'Diretoria', 'Fiscal'],
};

// 2. CARDS DO MÓDULO — paleta verde (Contábil). RTB12 é a tela-âncora (primeiro).
const modulesList = [
    {
        id: 'rtb12',
        label: 'RTB12 Simples Nacional',
        icon: DollarSign,
        color: '#16a34a',
        bgColor: '#dcfce7',
        desc: 'Controle do limite do Simples Nacional, rolling forecast, projeções e rateio de notas entre empresas.',
        stat: 'Simples Nacional'
    },
    {
        id: 'automacoes',
        label: 'Automações',
        icon: Zap,
        color: '#15803d',
        bgColor: '#dcfce7',
        desc: 'Ferramentas contábeis: análise de P&L (Z-Score) e conferência de vendas (Balancete × SOVOS).',
        stat: 'Automações'
    },
];

const ContabilidadePainel = () => {
    const userStr = localStorage.getItem('user');
    const user = userStr ? JSON.parse(userStr) : {};
    const userRole = user?.perfil || '';

    // Deep-link via querystring (ex.: card de pendências da Home): ?tab=rtb12.
    const [searchParams] = useSearchParams();
    const tabInicial = searchParams.get('tab');

    const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
    const [isCollapsed, setIsCollapsed] = useState(window.innerWidth <= 768);
    const [activeTab, setActiveTab] = useState(tabInicial || 'dashboard');
    const [isHelpOpen, setIsHelpOpen] = useState(false);

    // Só mostra os cards/abas que o perfil pode acessar.
    const allowedModules = modulesList.filter(mod => PERMISSIONS[mod.id]?.includes(userRole));

    useEffect(() => {
        const handleResize = () => {
            const mobile = window.innerWidth <= 768;
            setIsMobile(mobile);
            if (mobile) setIsCollapsed(true);
        };
        window.addEventListener('resize', handleResize);
        handleResize();
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    // Segurança: se cair numa aba sem permissão (ex.: troca de perfil), volta ao dashboard.
    useEffect(() => {
        if (activeTab !== 'dashboard' && !PERMISSIONS[activeTab]?.includes(userRole)) {
            setActiveTab('dashboard');
        }
    }, [activeTab, userRole]);

    const getMarginLeft = () => (window.innerWidth <= 768 ? '0px' : (isCollapsed ? '80px' : '260px'));

    // --- VISÃO GERAL (cards) ---
    const OverviewWidgets = () => (
        <div>
            {/* BANNER VERDE */}
            <div style={{ background: 'linear-gradient(135deg, #22c55e 0%, #15803d 100%)', borderRadius: '16px', padding: '40px', color: '#fff', marginBottom: '32px', position: 'relative', overflow: 'hidden', boxShadow: '0 10px 15px -3px rgba(22, 163, 74, 0.3)' }}>
                <div style={{ position: 'relative', zIndex: 10 }}>
                    <h2 style={{ fontSize: '32px', fontWeight: '800', marginBottom: '12px', marginTop: 0, color: '#fff' }}>Contábil</h2>
                    <p style={{ color: '#dcfce7', fontSize: '18px', maxWidth: '650px', margin: 0, lineHeight: '1.6' }}>Ferramentas contábeis do grupo: controle do Simples Nacional, análise de P&L e conferência de vendas.</p>
                </div>
                <div style={{ position: 'absolute', right: '-30px', top: '-40px', opacity: 0.15 }}>
                    <Calculator size={250} color="#ffffff" />
                </div>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '24px' }}>
                {allowedModules.map((mod) => (
                    <div key={mod.id} onClick={() => setActiveTab(mod.id)} style={{ backgroundColor: '#fff', borderRadius: '16px', padding: '24px', border: '1px solid #e2e8f0', display: 'flex', flexDirection: 'column', height: '100%', cursor: 'pointer', position: 'relative', overflow: 'hidden', transition: 'all 0.2s', boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.05)' }}>
                        <div style={{ position: 'absolute', left: 0, top: 0, bottom: 0, width: '4px', backgroundColor: mod.color }}></div>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '20px', width: '100%' }}>
                            <div>
                                <p style={{ fontSize: '12px', fontWeight: '800', textTransform: 'uppercase', color: mod.color, marginBottom: '8px', margin: 0 }}>{mod.stat}</p>
                                <h3 style={{ fontSize: '22px', fontWeight: '800', color: '#0f172a', margin: 0 }}>{mod.label}</h3>
                            </div>
                            <div style={{ padding: '12px', borderRadius: '12px', backgroundColor: mod.bgColor, color: mod.color }}>
                                <mod.icon size={26} />
                            </div>
                        </div>
                        <div style={{ marginTop: 'auto' }}>
                            <p style={{ fontSize: '14px', color: '#64748b', fontWeight: '500', lineHeight: '1.5', margin: '0 0 16px 0' }}>{mod.desc}</p>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '14px', fontWeight: '700', color: mod.color }}>
                                <span>Acessar</span> <ArrowRight size={16} />
                            </div>
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );

    // Verbete do Manual que abre expandido conforme a aba ativa.
    // (O botão "Manual da Tela" mostra TODO o módulo Contábil; isto só escolhe o ponto de partida.)
    const getSecaoAjuda = () => {
        switch (activeTab) {
            case 'rtb12':      return 'rtb12-simplesnacional';
            case 'automacoes': return 'pl-zscore';
            default:           return 'visao-geral-contabil';
        }
    };

    // Cada aba renderiza a ferramenta correspondente como conteúdo EMBUTIDO.
    const renderContent = () => {
        if (activeTab === 'dashboard') return <OverviewWidgets />;
        switch (activeTab) {
            case 'rtb12':      return <AutomacaoRTB12 embedded={true} />;
            case 'automacoes': return <ContabilidadeAutomacoes />;
            default:           return <OverviewWidgets />;
        }
    };

    return (
        <div style={{ display: 'flex', minHeight: '100vh', backgroundColor: '#f8fafc' }}>
            <Sidebar isCollapsed={isCollapsed} setIsCollapsed={setIsCollapsed} mostrarBotaoMobile={false} />
            <div style={{ flex: 1, marginLeft: getMarginLeft(), transition: 'margin-left 0.3s ease' }}>

                {/* CABEÇALHO — padrão dos módulos, na cor do Contábil (verde) */}
                <ModuloHeader
                    eyebrow="Módulo"
                    titulo="Contábil"
                    cor="#16a34a"
                    onAbrirManual={() => setIsHelpOpen(true)}
                    onAbrirMenu={() => setIsCollapsed(false)}
                    isMobile={isMobile}
                />

                <div style={{ padding: isMobile ? '20px' : '40px', maxWidth: '1600px', margin: '0 auto', width: '100%', boxSizing: 'border-box' }}>

                    {/* BARRA DE NAVEGAÇÃO (abas) */}
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px', marginBottom: '32px', backgroundColor: '#fff', padding: '8px', borderRadius: '12px', border: '1px solid #e2e8f0', width: '100%' }}>

                        <button onClick={() => setActiveTab('dashboard')} style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '10px 20px', borderRadius: '8px', border: 'none', fontWeight: '600', cursor: 'pointer', transition: 'all 0.2s', background: activeTab === 'dashboard' ? 'linear-gradient(135deg, #22c55e 0%, #15803d 100%)' : 'transparent', color: activeTab === 'dashboard' ? '#fff' : '#64748b', boxShadow: activeTab === 'dashboard' ? '0 4px 6px rgba(22, 163, 74, 0.3)' : 'none', whiteSpace: 'nowrap' }}>
                            <LayoutDashboard size={18} /> Visão Geral
                        </button>

                        <div style={{ minWidth: '1px', backgroundColor: '#e2e8f0', margin: '0 5px' }}></div>

                        {allowedModules.map((mod) => (
                            <button key={mod.id} onClick={() => setActiveTab(mod.id)} style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '10px 20px', borderRadius: '8px', fontWeight: '600', cursor: 'pointer', transition: 'all 0.2s', border: activeTab === mod.id ? `2px solid ${mod.color}` : '2px solid transparent', backgroundColor: activeTab === mod.id ? mod.bgColor : 'transparent', color: activeTab === mod.id ? mod.color : '#64748b', whiteSpace: 'nowrap' }}>
                                <mod.icon size={18} /> {mod.label}
                            </button>
                        ))}
                    </div>

                    {renderContent()}
                </div>
            </div>

            <HelpCenter
                isOpen={isHelpOpen}
                onClose={() => setIsHelpOpen(false)}
                secaoInicial={getSecaoAjuda()}
                modulo="contabil"
            />
        </div>
    );
};

export default ContabilidadePainel;
